import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiGift,
  FiDollarSign,
  FiTrendingUp,
  FiUsers,
  FiDownload,
  FiRefreshCw,
  FiBarChart2,
  FiPhone,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiAlertCircle,
  FiSettings,
  FiEdit,
  FiSearch
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminAPI, referralAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const RewardManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rewardStats, setRewardStats] = useState(null);
  const [rewardHistory, setRewardHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [rewardSettings, setRewardSettings] = useState({
    registrationBonus: 200,
    referralBonus: 100,
    autoRewardEnabled: true,
    minimumReferrals: 1
  });
  const { showSuccess, showError } = useNotification();

  const fetchRewardStats = useCallback(async () => {
    try {
      const response = await referralAPI.getAdminStats();
      setRewardStats(response.data);
    } catch (error) {
      showError('Failed to load reward statistics');
    }
  }, [showError]);

  const fetchRewardHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getRewardHistory({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        status: filterStatus,
        type: filterType,
        timeRange: selectedTimeRange
      });
      const payload = response?.data?.data || response?.data || {};
      const rewards = Array.isArray(payload?.rewards) ? payload.rewards : [];
      setRewardHistory(rewards);
      setTotalPages(payload?.pagination?.pages || 1);
    } catch (error) {
      showError('Failed to load reward history');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterStatus, filterType, selectedTimeRange, showError]);

  useEffect(() => {
    fetchRewardStats();
    fetchRewardHistory();
  }, [fetchRewardStats, fetchRewardHistory]);

  useEffect(() => {
    const path = location.pathname || '';
    if (path.endsWith('/history')) setActiveTab('history');
    else if (path.endsWith('/analytics')) setActiveTab('analytics');
    else if (path.endsWith('/settings')) setActiveTab('settings');
    else setActiveTab('overview');
  }, [location.pathname]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await adminAPI.getRewardSettings();
        const settings = response?.data?.data || response?.data;
        if (settings) {
          setRewardSettings((prev) => ({
            registrationBonus: settings?.registrationRewards?.newUserBonus ?? prev.registrationBonus,
            referralBonus: settings?.registrationRewards?.referrerBonus ?? prev.referralBonus,
            autoRewardEnabled: !!settings?.payoutSettings?.autoPayoutEnabled,
            minimumReferrals: prev.minimumReferrals
          }));
        }
      } catch (_) {}
    };
    if (activeTab === 'settings') loadSettings();
  }, [activeTab]);

  const handleRetryReward = async (rewardId) => {
    try {
      const response = await adminAPI.retryReward(rewardId);
      const success = response?.data?.success ?? response?.success;
      if (success) {
        showSuccess('Reward retry initiated');
        fetchRewardHistory();
        fetchRewardStats();
      } else {
        const message = response?.data?.message || response?.message;
        showError(message || 'Failed to retry reward');
      }
    } catch (error) {
      showError('Failed to retry reward');
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      const response = await adminAPI.updateRewardSettings(rewardSettings);
      if (response.success) {
        showSuccess('Reward settings updated');
        navigate('/admin/rewards');
      } else {
        showError(response.message || 'Failed to update settings');
      }
    } catch (error) {
      showError('Failed to update reward settings');
    }
  };

  const handleExportRewards = async () => {
    try {
      const response = await adminAPI.exportRewardHistory({
        search: searchTerm,
        status: filterStatus,
        type: filterType,
        timeRange: selectedTimeRange
      });
      const data = Array.isArray(response?.data) ? response.data : (response?.data?.data || response?.data?.rewards || []);
      const csvContent = convertToCSV(data);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `reward-history-${new Date().toISOString().split('T')[0]}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      showSuccess('Reward history exported');
    } catch (error) {
      showError('Failed to export reward history');
    }
  };

  const convertToCSV = (data) => {
    const headers = ['Recipient Name', 'Email', 'Type', 'Amount', 'Phone Number', 'Status', 'Date'];
    const csvRows = [headers.join(',')];
    (Array.isArray(data) ? data : []).forEach((reward) => {
      const row = [
        `"${(reward.recipientName || '').replace(/"/g, '""')}"`,
        `"${(reward.recipientEmail || '').replace(/"/g, '""')}"`,
        `"${reward.type === 'registration_bonus' ? 'Registration' : 'Referral'}"`,
        reward.amount ?? '',
        `"${(reward.phoneNumber || '').replace(/"/g, '""')}"`,
        `"${(reward.status || '').replace(/"/g, '""')}"`,
        `"${reward.createdAt ? new Date(reward.createdAt).toLocaleDateString() : ''}"`
      ];
      csvRows.push(row.join(','));
    });
    return csvRows.join('\n');
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);
  const formatNumber = (num) => new Intl.NumberFormat().format(num || 0);

  const getStatusBadge = (status) => {
    const map = {
      completed: 'bg-emerald-100 text-emerald-800',
      success: 'bg-emerald-100 text-emerald-800',
      sent: 'bg-emerald-100 text-emerald-800',
      pending: 'bg-amber-100 text-amber-800',
      processing: 'bg-amber-100 text-amber-800',
      failed: 'bg-red-100 text-red-800',
      error: 'bg-red-100 text-red-800'
    };
    return map[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed' || status === 'success' || status === 'sent') return <FiCheckCircle className="h-4 w-4" />;
    if (status === 'pending' || status === 'processing') return <FiClock className="h-4 w-4" />;
    if (status === 'failed' || status === 'error') return <FiXCircle className="h-4 w-4" />;
    return <FiAlertCircle className="h-4 w-4" />;
  };

  if (loading && !rewardStats && activeTab !== 'settings') {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Reward Management</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track and manage reward distributions.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 3 months</option>
              <option value="1y">Last year</option>
            </select>
            <button
              type="button"
              onClick={() => navigate('/admin/rewards/settings')}
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
            >
              <FiSettings className="h-4 w-4" />
              Settings
            </button>
            <button
              type="button"
              onClick={() => { fetchRewardStats(); fetchRewardHistory(); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
            >
              <FiRefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            type="button"
            onClick={() => navigate('/admin/rewards')}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'overview' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiBarChart2 className="h-4 w-4" />
            Overview
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/rewards/history')}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'history' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiGift className="h-4 w-4" />
            Reward History
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/rewards/analytics')}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'analytics' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiTrendingUp className="h-4 w-4" />
            Analytics
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/rewards/settings')}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <FiSettings className="h-4 w-4" />
            Settings
          </button>
        </div>

        {/* Overview */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiGift className="h-4 w-4" />
                  <span className="text-sm font-medium">Total Rewards</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">{formatNumber(rewardStats?.totalRewards)}</div>
                <div className="text-xs text-slate-500 mt-0.5">+{rewardStats?.newRewardsThisPeriod || 0} this period</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiDollarSign className="h-4 w-4" />
                  <span className="text-sm font-medium">Total Value</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">{formatCurrency(rewardStats?.totalValue || 0)}</div>
                <div className="text-xs text-slate-500 mt-0.5">+{formatCurrency(rewardStats?.valueThisPeriod || 0)} this period</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiTrendingUp className="h-4 w-4" />
                  <span className="text-sm font-medium">Success Rate</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">{((rewardStats?.successfulRewards / rewardStats?.totalRewards) * 100 || 0).toFixed(1)}%</div>
                <div className="text-xs text-slate-500 mt-0.5">{rewardStats?.successfulRewards || 0} / {rewardStats?.totalRewards || 0} successful</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                  <FiUsers className="h-4 w-4" />
                  <span className="text-sm font-medium">Unique Recipients</span>
                </div>
                <div className="text-2xl font-semibold text-slate-900">{formatNumber(rewardStats?.uniqueRecipients)}</div>
                <div className="text-xs text-slate-500 mt-0.5">Across {rewardStats?.totalRewards || 0} rewards</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                  <h3 className="text-sm font-medium text-slate-900">Reward Distribution</h3>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Registration Bonuses</span>
                    <div className="text-right">
                      <span className="font-medium text-slate-900">{rewardStats?.registrationBonuses || 0}</span>
                      <span className="text-slate-500 text-sm ml-2">{formatCurrency((rewardStats?.registrationBonuses || 0) * 200)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Referral Bonuses</span>
                    <div className="text-right">
                      <span className="font-medium text-slate-900">{rewardStats?.referralBonuses || 0}</span>
                      <span className="text-slate-500 text-sm ml-2">{formatCurrency((rewardStats?.referralBonuses || 0) * 100)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                  <h3 className="text-sm font-medium text-slate-900">Status Breakdown</h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600"><FiCheckCircle className="h-5 w-5" /></div>
                    <div className="flex-1"><span className="text-sm text-slate-600">Successful</span></div>
                    <span className="font-semibold text-slate-900">{rewardStats?.successfulRewards || 0}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600"><FiClock className="h-5 w-5" /></div>
                    <div className="flex-1"><span className="text-sm text-slate-600">Pending</span></div>
                    <span className="font-semibold text-slate-900">{rewardStats?.pendingRewards || 0}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center text-red-600"><FiXCircle className="h-5 w-5" /></div>
                    <div className="flex-1"><span className="text-sm text-slate-600">Failed</span></div>
                    <span className="font-semibold text-slate-900">{rewardStats?.failedRewards || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-900">Recent Reward Activity</h3>
                <button type="button" onClick={() => { fetchRewardStats(); fetchRewardHistory(); }} className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
                  <FiRefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              </div>
              <div className="p-4">
                {rewardStats?.recentRewards?.length ? (
                  <ul className="divide-y divide-slate-100">
                    {rewardStats.recentRewards.map((activity, index) => {
                      const normalizedStatus = activity?.status === 'sent' ? 'completed' : activity?.status;
                      const title = `${activity?.userName || 'User'} ${activity?.type === 'registration_bonus' ? 'received registration bonus' : 'received referral bonus'}`;
                      const details = `${activity?.phoneNumber || ''} · ${formatCurrency(activity?.amount || 0)} · ${activity?.date ? new Date(activity.date).toLocaleString() : ''}`;
                      return (
                        <li key={index} className="py-3 flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">{getStatusIcon(normalizedStatus)}</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900">{title}</div>
                            <div className="text-xs text-slate-500">{details}</div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${getStatusBadge(normalizedStatus)}`}>{normalizedStatus}</span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500 py-4">No recent activity.</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* History */}
        {activeTab === 'history' && (
          <>
            <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
              <div className="relative flex-1 max-w-sm">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  placeholder="Search by phone or user..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400"
                />
              </div>
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                <option value="all">All status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
                <option value="all">All types</option>
                <option value="registration_bonus">Registration Bonus</option>
                <option value="referral_bonus">Referral Bonus</option>
              </select>
              <button type="button" onClick={handleExportRewards} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50">
                <FiDownload className="h-4 w-4" />
                Export
              </button>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              {loading ? (
                <div className="flex justify-center py-16"><LoadingSpinner /></div>
              ) : !Array.isArray(rewardHistory) || rewardHistory.length === 0 ? (
                <div className="text-center py-14 px-6">
                  <FiGift className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No reward history match your filters.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/80">
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Recipient</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Type</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Amount</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Phone</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-700">Date</th>
                        <th className="px-4 py-3 w-0" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rewardHistory.map((reward) => (
                        <tr key={reward._id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-slate-900">{reward.recipientName}</div>
                              <div className="text-xs text-slate-500">{reward.recipientEmail}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {reward.type === 'registration_bonus' ? 'Registration' : 'Referral'}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-900">{formatCurrency(reward.amount)}</td>
                          <td className="px-4 py-3 text-slate-600 flex items-center gap-1.5">
                            <FiPhone className="h-3.5 w-3.5 text-slate-400" />
                            {reward.phoneNumber}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(reward.status)}`}>{reward.status}</span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            <div>{reward.createdAt ? new Date(reward.createdAt).toLocaleDateString() : '—'}</div>
                            <div className="text-xs text-slate-500">{reward.createdAt ? new Date(reward.createdAt).toLocaleTimeString() : ''}</div>
                          </td>
                          <td className="px-4 py-3">
                            {reward.status === 'failed' && (
                              <button type="button" onClick={() => handleRetryReward(reward._id)} className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50">
                                <FiRefreshCw className="h-3.5 w-3.5" /> Retry
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between px-2">
                <span className="text-xs text-slate-500">Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Previous</button>
                  <button type="button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none">Next</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Analytics */}
        {activeTab === 'analytics' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                <h3 className="text-sm font-medium text-slate-900">Reward Trends</h3>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Daily Average</span>
                  <span className="font-semibold text-slate-900">{formatNumber(rewardStats?.dailyAverage)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Weekly Growth</span>
                  <span className="font-semibold text-emerald-600">+{rewardStats?.weeklyGrowth || 0}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Monthly Total</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(rewardStats?.monthlyTotal)}</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80">
                <h3 className="text-sm font-medium text-slate-900">Performance Metrics</h3>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Avg. Processing Time</span>
                  <span className="font-semibold text-slate-900">{rewardStats?.avgProcessingTime || 0} mins</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Retry Rate</span>
                  <span className="font-semibold text-slate-900">{rewardStats?.retryRate || 0}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Cost per Reward</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(rewardStats?.costPerReward)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 max-w-lg">
            <h3 className="text-sm font-medium text-slate-900 mb-4">Reward Settings</h3>
            <form onSubmit={handleUpdateSettings} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Registration Bonus (₦)</label>
                <input
                  type="number"
                  value={rewardSettings.registrationBonus}
                  onChange={(e) => setRewardSettings((s) => ({ ...s, registrationBonus: parseInt(e.target.value, 10) || 0 }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  min={0}
                  step={50}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Referral Bonus (₦)</label>
                <input
                  type="number"
                  value={rewardSettings.referralBonus}
                  onChange={(e) => setRewardSettings((s) => ({ ...s, referralBonus: parseInt(e.target.value, 10) || 0 }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  min={0}
                  step={50}
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="autoReward"
                  checked={rewardSettings.autoRewardEnabled}
                  onChange={(e) => setRewardSettings((s) => ({ ...s, autoRewardEnabled: e.target.checked }))}
                  className="rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                />
                <label htmlFor="autoReward" className="text-sm font-medium text-slate-700">Enable automatic reward distribution</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Minimum Referrals for Bonus</label>
                <input
                  type="number"
                  value={rewardSettings.minimumReferrals}
                  onChange={(e) => setRewardSettings((s) => ({ ...s, minimumReferrals: parseInt(e.target.value, 10) || 1 }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400"
                  min={1}
                  required
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => navigate('/admin/rewards')} className="px-4 py-2.5 text-sm font-medium border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800">
                  <FiEdit className="h-4 w-4" />
                  Update Settings
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardManagement;
